import { observable, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"


export class DropZoneModel extends Model {
  private readonly input = document.createElement("input")

  readonly accept: string[]
  readonly multiple: boolean
  readonly onFileSelected?: (file: File) => void
  readonly onFilesSelected?: (files: File[]) => void

  readonly isDragOver = observable.box(false)
  readonly hasError = observable.box(false)

  constructor(params: {
    accept?: string | string[]
    multiple?: boolean

    onFileSelected?: (file: File) => void
    onFilesSelected?: (files: File[]) => void
  }) {
    super("DropZoneModel")

    const accept = typeof params.accept === "string" ? [params.accept] : params.accept || []
    this.accept = accept.filter(Boolean)

    this.multiple = params.multiple || false
    this.onFileSelected = params.onFileSelected
    this.onFilesSelected = params.onFilesSelected

    this.setupInput()
  }

  element: HTMLElement | null = null

  readonly refCallback = (element: HTMLDivElement | null) => {
    if (!element || this.element === element) {
      return
    }

    element.addEventListener("click", this.onClick)
    element.addEventListener("dragover", this.onDragOver)
    element.addEventListener("dragleave", this.onDragLeave)
    element.addEventListener("drop", this.onDrop)

    this.lifetime.add(() => {
      element.removeEventListener("click", this.onClick)
      element.removeEventListener("dragover", this.onDragOver)
      element.removeEventListener("dragleave", this.onDragLeave)
      element.removeEventListener("drop", this.onDrop)
    }, "DropZoneModel:refCallback")
  }

  readonly onClick = () => {
    this.input.click()
  }

  readonly onDragOver = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy"
    }

    runInAction(() => {
      this.isDragOver.set(true)
      this.hasError.set(false)
    })
  }

  readonly onDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    runInAction(() => {
      this.isDragOver.set(false)
    })
  }

  readonly onDrop = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    runInAction(() => {
      this.isDragOver.set(false)
    })

    this.onChange(event)
  }

  private setupInput() {
    this.input.style.display = "none"

    this.input.type = "file"
    this.input.accept = this.accept.join(",")
    this.input.multiple = this.multiple

    this.input.addEventListener("change", this.onChange)

    document.body.appendChild(this.input)

    this.lifetime.add(() => {
      this.input.removeEventListener("change", this.onChange)
      this.input.parentElement?.removeChild(this.input)
    }, "DropZoneModel:setupInput")
  }

  private readonly onChange = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement
    if (!target) {
      return
    }

    let files = Array.from(target.files || [])
    if (event instanceof DragEvent) {
      files = Array.from(event.dataTransfer?.files || [])
    }

    files = files.filter(this.isFileAccepted)

    if (!files.length) {
      return runInAction(() => {
        this.hasError.set(true)
      })
    }

    runInAction(() => {
      this.hasError.set(false)
    })

    // Reset input value to allow re-selecting the same file
    this.input.value = ""

    if (this.multiple) {
      const selectedFiles = files.filter(Boolean)
      return this.onFilesSelected?.(selectedFiles)
    }

    const file = files[0]
    file && this.onFileSelected?.(file)
  }

  private isFileAccepted = (file: File) => {
    if (!this.accept.length) {
      return true
    }

    return this.accept.some(accept => {
      if (file.type === accept) {
        return true
      }

      if (accept.endsWith("/*")) {
        const typePrefix = accept.slice(0, -2)
        return file.type.startsWith(typePrefix + "/")
      }

      return false
    })
  }
}